import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import {
  Clock,
  ChefHat,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Zap,
} from "lucide-react";
import { AdminLayout } from "../components/AdminLayout";

const API = process.env.REACT_APP_BACKEND_URL || "";

const KitchenPanel = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    auto_refresh_enabled: true,
    refresh_interval_seconds: 10,
  });
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);

  useEffect(() => {
    fetchSettings();
    fetchOrders();

    // Set up auto-refresh
    const interval = setInterval(() => {
      if (settings.auto_refresh_enabled) {
        fetchOrders(true); // Silent refresh
      }
    }, (settings.refresh_interval_seconds || 10) * 1000);

    return () => clearInterval(interval);
  }, [settings.auto_refresh_enabled, settings.refresh_interval_seconds]);

  useEffect(() => {
    fetchOrders();
  }, [page]);

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/api/kitchen/settings`, {
        headers: getAuthHeader(),
      });
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
      // Use defaults if settings fetch fails
    }
  };

  const fetchOrders = async (silent = false) => {
    try {
      if (!silent) setLoading(true);

      const response = await axios.get(`${API}/api/kitchen/orders`, {
        headers: getAuthHeader(),
        params: { page, limit },
      });

      setOrders(response.data.data);
      setTotalPages(response.data.total_pages);
      setTotalOrders(response.data.total);
      setLastRefresh(new Date());
    } catch (error) {
      console.error("Error fetching orders:", error);
      if (!silent) toast.error("Failed to load orders");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(
        `${API}/api/kitchen/orders/${orderId}/status`,
        { status: newStatus },
        { headers: getAuthHeader() }
      );

      toast.success(`Order updated to ${newStatus}`);
      fetchOrders(true); // Silent refresh
    } catch (error) {
      console.error("Error updating order status:", error);
      toast.error("Failed to update order");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "new":
        return "bg-blue-100 text-blue-800 border-blue-300";
      case "in_progress":
        return "bg-yellow-100 text-yellow-800 border-yellow-300";
      case "ready":
        return "bg-green-100 text-green-800 border-green-300";
      case "cancelled":
        return "bg-gray-200 text-gray-600 border-gray-300"; // new style
      default:
        return "bg-gray-100 text-gray-800 border-gray-300";
    }
  };

  const getOverdueTime = (order) => {
    if (!order.eta) return null;

    const now = new Date();
    const estimatedReady = new Date(order.eta.estimated_ready_time_str);
    const diffMinutes = Math.floor((now - estimatedReady) / 60000);

    return diffMinutes > 0 ? diffMinutes : 0;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "new":
        return <AlertCircle className="w-5 h-5" />;
      case "in_progress":
        return <ChefHat className="w-5 h-5" />;
      case "ready":
        return <CheckCircle className="w-5 h-5" />;
      default:
        return null;
    }
  };

  const getNextStatus = (currentStatus) => {
    if (currentStatus === "cancelled") return null; // no next status
    switch (currentStatus) {
      case "new":
        return "in_progress";
      case "in_progress":
        return "ready";
      default:
        return null;
    }
  };

  const getNextStatusLabel = (currentStatus) => {
    if (currentStatus === "cancelled") return null;
    switch (currentStatus) {
      case "new":
        return "Start Cooking";
      case "in_progress":
        return "Mark Ready";
      default:
        return null;
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMinutes = Math.floor((now - date) / 60000);

    if (diffMinutes < 1) return "Just now";
    if (diffMinutes === 1) return "1 min ago";
    if (diffMinutes < 60) return `${diffMinutes} mins ago`;

    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours === 1) return "1 hour ago";
    return `${diffHours} hours ago`;
  };

  const getElapsedTime = (startTime) => {
    if (!startTime) return null;
    const start = new Date(startTime);
    const now = new Date();
    const diffMinutes = Math.floor((now - start) / 60000);
    return diffMinutes;
  };

  const getPriorityBadge = (priority) => {
    if (priority === "high") {
      return (
        <span className="ml-2 bg-red-600 text-white text-xs px-2 py-0.5 rounded-full">
          🔥 PRIORITY
        </span>
      );
    }

    if (priority === "low") {
      return (
        <span className="ml-2 bg-gray-300 text-gray-700 text-xs px-2 py-0.5 rounded-full">
          Low
        </span>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </AdminLayout>
    );
  }
  const sortedOrders = [...orders].sort((a, b) => {
    if (a.is_overdue && !b.is_overdue) return -1;
    if (!a.is_overdue && b.is_overdue) return 1;

    if (a.priority === "high" && b.priority !== "high") return -1;
    if (a.priority !== "high" && b.priority === "high") return 1;

    return new Date(a.received_at) - new Date(b.received_at);
  });

  return (
    <AdminLayout>
      <div className="min-h-screen bg-gray-50 p-6">
        {/* Header */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <ChefHat className="w-8 h-8 text-indigo-600" />
              Kitchen Panel
            </h1>
            <p className="text-gray-600 mt-1">
              Last updated: {lastRefresh.toLocaleTimeString()}
            </p>
          </div>

          <button
            onClick={() => {
              setPage(1);
              fetchOrders();
            }}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-600 text-sm font-medium">New Orders</p>
                <p className="text-3xl font-bold text-blue-900">
                  {orders.filter((o) => o.status === "new").length}
                </p>
              </div>
              <AlertCircle className="w-10 h-10 text-blue-400" />
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-600 text-sm font-medium">
                  In Progress
                </p>
                <p className="text-3xl font-bold text-yellow-900">
                  {orders.filter((o) => o.status === "in_progress").length}
                </p>
              </div>
              <ChefHat className="w-10 h-10 text-yellow-400" />
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-600 text-sm font-medium">Ready</p>
                <p className="text-3xl font-bold text-green-900">
                  {orders.filter((o) => o.status === "ready").length}
                </p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
          </div>
        </div>

        {/* Orders Grid */}
        {orders.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <ChefHat className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No active orders</p>
            <p className="text-gray-400 text-sm mt-2">
              New orders will appear here automatically
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {sortedOrders.map((order) => {
              const showOverdue =
                order.is_overdue &&
                order.status !== "ready" &&
                order.status !== "cancelled";
              return (
                <div
                  key={order.order_id}
                  className={`bg-white rounded-lg shadow-sm border-2 overflow-hidden
                  ${
                    showOverdue ? "border-red-600 " : "border-gray-200"
                  } //animate-pulse
                `}
                >
                  {/* ================= Order Header ================= */}
                  <div
                    className={`p-4 ${getStatusColor(order.status)} border-b-2`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(order.status)}
                        <span className="font-bold text-lg uppercase">
                          {order.status.replace("_", " ")}
                        </span>
                        {getPriorityBadge(order.priority)}
                      </div>

                      <span className="text-sm font-medium">
                        {order.table_name || "Takeaway"}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="w-4 h-4" />
                      <span>{formatTime(order.received_at)}</span>

                      {order.started_at && (
                        <span className="ml-auto font-medium">
                          {getElapsedTime(order.started_at)} min
                        </span>
                      )}
                    </div>

                    {/* ETA */}
                    {order.eta && (
                      <div className="mt-2 pt-2 border-t border-current border-opacity-20">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium">ETA:</span>
                          <div className="text-right">
                            <div className="font-bold">
                              {order.eta.estimated_ready_time_str}
                            </div>
                            <div className="text-xs opacity-90">
                              (in {order.eta.total_minutes} min)
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* OVERDUE */}
                    {showOverdue && (
                      <div className="mt-2 text-xs font-bold text-red-700 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        <span>OVERDUE</span>
                      </div>
                    )}
                  </div>

                  {/* ================= Order Items ================= */}
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-700 mb-3">Items:</h4>

                    <div className="space-y-2">
                      {order.items.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex justify-between items-start"
                        >
                          <div className="flex-1">
                            <span className="font-medium text-gray-900">
                              {item.quantity}x {item.item_name}
                            </span>

                            {item.modifiers?.length > 0 && (
                              <div className="text-xs text-gray-500 ml-6 space-y-0.5">
                                {item.modifiers.map((mod, i) => (
                                  <div key={i}>
                                    • {mod.group_name}:{" "}
                                    <span className="font-medium">
                                      {mod.option_name}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {item.notes && (
                              <div className="text-xs text-gray-600 ml-6 italic">
                                Note: {item.notes}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {order.created_by && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <span className="text-xs text-gray-500">
                          Order by: {order.created_by}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* ================= Actions ================= */}
                  <div className="p-4 bg-gray-50 border-t border-gray-200">
                    {order.status !== "cancelled" &&
                    getNextStatus(order.status) ? (
                      <button
                        onClick={() =>
                          updateOrderStatus(
                            order.order_id,
                            getNextStatus(order.status)
                          )
                        }
                        className="w-full bg-indigo-600 text-white px-4 py-3 rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                      >
                        {getNextStatusLabel(order.status)}
                      </button>
                    ) : order.status !== "cancelled" ? (
                      <div className="text-center text-sm text-green-600 font-medium py-2 flex items-center justify-center gap-1">
                        <CheckCircle className="w-4 h-4" />
                        <span>Ready for serving</span>
                      </div>
                    ) : (
                      <div className="text-center text-sm text-red-600 font-medium py-2 flex items-center justify-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        <span>Cancelled</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-8 bg-white p-4 rounded-lg shadow-sm border">
            <span className="text-sm text-gray-600">
              Showing page <b>{page}</b> of <b>{totalPages}</b> ({totalOrders}{" "}
              orders)
            </span>

            <div className="flex gap-2">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                className={`px-3 py-1.5 rounded-md text-sm font-medium border
          ${
            page === 1
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-white text-gray-700 hover:bg-gray-100"
          }`}
              >
                Previous
              </button>

              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => setPage(i + 1)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium border
            ${
              page === i + 1
                ? "bg-indigo-600 text-white"
                : "bg-white text-gray-700 hover:bg-gray-100"
            }`}
                >
                  {i + 1}
                </button>
              ))}

              <button
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                className={`px-3 py-1.5 rounded-md text-sm font-medium border
          ${
            page === totalPages
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-white text-gray-700 hover:bg-gray-100"
          }`}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default KitchenPanel;
